import { useState, useCallback } from "react";

export interface Preferences {
  slippageBps: number;
  theme: "dark" | "light";
  disabledProviders: string[];
  degenMode: boolean;
}

const STORAGE_KEY = "crossllama-preferences";

const DEFAULTS: Preferences = {
  slippageBps: 0, // 0 = auto
  theme: "dark",
  disabledProviders: [],
  degenMode: false,
};

function loadPrefs(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
}

function savePrefs(prefs: Preferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export function usePreferences() {
  const [prefs, setPrefs] = useState<Preferences>(loadPrefs);

  const update = useCallback((partial: Partial<Preferences>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...partial };
      savePrefs(next);
      if (partial.theme) {
        document.documentElement.classList.toggle("light", partial.theme === "light");
      }
      return next;
    });
  }, []);

  const toggleProvider = useCallback(
    (provider: string) => {
      const disabled = prefs.disabledProviders;
      const next = disabled.includes(provider)
        ? disabled.filter((p) => p !== provider)
        : [...disabled, provider];
      update({ disabledProviders: next });
    },
    [prefs.disabledProviders, update],
  );

  return { prefs, update, toggleProvider };
}
